import React from "react";
import PageFrame from "./PageFrame";

/**
 * Page 03 - Rent Roll.
 *
 * Paginates: the caller slices rows into chunks and renders one RentRoll per chunk. The
 * Occupied / Vacant / Total footer is printed on the final chunk only.
 *
 * ---------------------------------------------------------------------------
 * Why the columns are mapped explicitly
 *
 * getRentRoll() in ../../PrintMultiTenant/tables.js produces rows and a footer whose
 * cells do NOT line up with each other, and neither matches the twelve columns the
 * template prints:
 *
 *   rows    3 cells from current-rr-tab general-information (Suite, Tenant, Sq Ft)
 *           + 11 from marketing-tab rent-roll, then `splice(10, 1)` drops Escalation
 *           PSF - leaving 13 cells, one more than the template has columns. The extra
 *           is "Option #1 Rent", which sits between Options and Lease Type and prints
 *           as a second blank column.
 *
 *   footer  12 slots written at the UNSPLICED offsets, so from Escalation PSF onwards
 *           it is one place to the left of the rows. That is why the totals drift out
 *           from under their headings.
 *
 * Rather than rely on either sequence lining up, each column below names its own source
 * index for rows and for the footer. Columns this page does not print (Escalation PSF,
 * Option #1 Rent, Lease Type) simply have no entry.
 * ---------------------------------------------------------------------------
 */

const COLUMNS = [
    // Widths total 914px, the printable width inside the card. Tenant takes the most
    // slack because it must never wrap; the date and amount columns are sized to their
    // widest realistic value rather than their heading, which is allowed to wrap.
    //
    // Lease Type is not printed: it is a single-tenant concept, and a multi-tenant roll
    // has a lease type per tenant rather than one for the property. The 76px it used to
    // occupy is shared out below.
    {label: 'Suite',             row: 0,  foot: 0,    width: 46,  align: 'left'},
    {label: 'Tenant',            row: 1,  foot: 1,    width: 190, align: 'left', nowrap: true},
    {label: 'Sq Ft',             row: 2,  foot: 2,    width: 62,  align: 'right'},
    {label: '% of Sq Ft',        row: 3,  foot: 3,    width: 78,  align: 'right'},
    {label: 'Start',             row: 4,  foot: 4,    width: 70,  align: 'left'},
    {label: 'End',               row: 5,  foot: 5,    width: 70,  align: 'left'},
    {label: 'Annual Rent',       row: 6,  foot: 6,    width: 96,  align: 'left'},
    {label: 'PSF',               row: 7,  foot: 7,    width: 60,  align: 'left'},
    {label: 'Escalation Date',   row: 8,  foot: 8,    width: 82,  align: 'left'},
    {label: 'Escalation Amount', row: 9,  foot: 9,    width: 96,  align: 'left'},
    // Ranged right at the business's request, 2026-09-12. It is the last column, so its
    // values and heading now sit against the card's right edge instead of hanging off the
    // left of a column with nothing after it. `align` drives the heading and the values
    // together, the same way Sq Ft and % of Sq Ft are already ranged right.
    {label: 'Options',           row: 10, foot: 11,   width: 64,  align: 'right'},
];

export default function RentRoll({rentRoll, subProduct, isLastChunk, pageNo, totalPages}) {
    return (
        <PageFrame
            eyebrow={`OFFERING SUMMARY  •  ${subProduct}`}
            title='Rent '
            titleAccent='Roll.'
            pageNo={pageNo}
            totalPages={totalPages}
        >
            <div className='bov-card'>
                <div className='bov-rr'>
                    <div className='bov-rr__row bov-rr__row--head'>
                        {COLUMNS.map(column => (
                            <Cell key={column.label} column={column} head>
                                {column.label}
                            </Cell>
                        ))}
                    </div>

                    {(rentRoll.rows || []).map((row, i) => (
                        <div key={i} className='bov-rr__row'>
                            {COLUMNS.map(column => (
                                <Cell key={column.label} column={column}>
                                    {valueAt(row.valueCells, column.row)}
                                </Cell>
                            ))}
                        </div>
                    ))}

                    {isLastChunk && rentRoll.footer ? (
                        <div className='bov-rr__footer'>
                            {rentRoll.footer.map((footerRow, i) => (
                                <div
                                    key={i}
                                    className={`bov-rr__row${i === rentRoll.footer.length - 1 ? ' bov-rr__row--total' : ''}`}
                                >
                                    {COLUMNS.map(column => (
                                        <Cell key={column.label} column={column}>
                                            {column.foot === null ? '' : footerRow[column.foot]}
                                        </Cell>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </PageFrame>
    );
}

function Cell({column, head, children}) {
    return (
        <div
            className={`bov-rr__cell bov-rr__cell--${column.align}${column.nowrap ? ' bov-rr__cell--nowrap' : ''}`}
            style={{width: `${column.width}px`}}
        >
            {head ? <span className='bov-rr__headLabel'>{children}</span> : children}
        </div>
    );
}

function valueAt(cells, index) {
    const cell = (cells || [])[index];
    return cell ? cell.value : '';
}
